import { useCallback, useRef, useEffect } from 'react';
import DashboardService from '../services/DashboardService';
import { showSuccessMessage, showErrorMessage } from '../utils/dashboardMessageUtils';
import { createNewChartTemplate } from '../utils/dashboardHelpers';
import { scrollToNewComponent } from '../utils/dashboardLayoutUtils';
import { useDashboardContext } from './useDashboardContext';
import { useDashboardData } from './useDashboardData';
import { useDashboardModals } from './useDashboardModals';

/**
 * @typedef {Object} DashboardTemplate
 * @property {string} dashboard_id - 대시보드 ID
 * @property {string} dashboardId - 대시보드 ID (alternative property)
 * @property {string} title - 대시보드 제목
 * @property {Array} components - 컴포넌트 목록
 * @property {Object} layout - 레이아웃 정보
 * @property {Object} dashboard_options - 대시보드 옵션
 */

/**
 * @typedef {Object} ComponentTemplate
 * @property {string} id - 컴포넌트 ID
 * @property {string} title - 컴포넌트 제목
 * @property {string} type - 컴포넌트 타입
 * @property {Object} position - 위치 정보
 * @property {number} position.x - X 좌표
 * @property {number} position.y - Y 좌표
 * @property {number} position.w - 너비
 * @property {number} position.h - 높이
 */

/**
 * @typedef {Object} LayoutItem
 * @property {string} i - 아이템 ID
 * @property {number} x - X 좌표
 * @property {number} y - Y 좌표
 * @property {number} w - 너비
 * @property {number} h - 높이
 * @property {number} minW - 최소 너비
 * @property {number} minH - 최소 높이
 */

/**
 * 대시보드 액션을 관리하는 커스텀 훅
 * @param {Object} props - 훅 매개변수
 * @returns {Object} 대시보드 액션 함수들
 */
export const useDashboardActions = ({
  dashboardTemplate: localDashboardTemplate,
  setDashboardTemplateAndUpdateData,
  layouts,
  setLayouts,
  addComponentToLayout,
  removeComponentFromLayout,
  toggleEditMode,
  editMode,
  setHasLayoutChanged,
  componentToUpdate,
  closeNLComponentModal,
  openNLComponentModal,
  savePreActionState,
  enableEditModeWithHistoryPreservation,
  enableEditModeWithoutBackup,
  // 충돌 모달 관련 함수 추가
  openConflictModal
}) => {
  // 컨텍스트에서 dashboardTemplate 가져오기
  const { dashboardTemplate: contextDashboardTemplate } = useDashboardContext();
  const dashboardTemplate = contextDashboardTemplate || localDashboardTemplate;

  // useRef를 사용하여 최신 dashboardTemplate 값을 항상 참조
  const dashboardTemplateRef = useRef(dashboardTemplate);
  
  // dashboardTemplate이 변경될 때마다 ref 업데이트
  useEffect(() => {
    dashboardTemplateRef.current = dashboardTemplate;
  }, [dashboardTemplate]);

  /**
   * 편집 모드 진입을 처리합니다.
   * @param {boolean} wasEditMode - 이전 편집 모드 상태
   */
  const handleEditModeEntry = useCallback((wasEditMode) => {
    if (!wasEditMode) {
      enableEditModeWithHistoryPreservation();
    } else {
    }
  }, [enableEditModeWithHistoryPreservation]);

  /**
   * 컴포넌트 템플릿과 레이아웃 아이템을 결합하여 최종 컴포넌트를 생성합니다.
   * @param {ComponentTemplate} componentTemplate - 컴포넌트 템플릿
   * @param {LayoutItem} layoutItem - 레이아웃 아이템
   * @returns {ComponentTemplate} position 정보가 포함된 최종 컴포넌트
   */
  const createFinalComponent = useCallback((componentTemplate, layoutItem) => {
    const finalComponent = {
      ...componentTemplate,
      position: { 
        x: layoutItem.x, 
        y: layoutItem.y, 
        w: layoutItem.w, 
        h: layoutItem.h 
      }
    };
    
    // w, h는 position에 포함되므로 최상위에서 제거
    delete finalComponent.w;
    delete finalComponent.h;
    
    return finalComponent;
  }, []);

  /**
   * 기존 템플릿에 새 컴포넌트를 추가하여 업데이트된 템플릿을 반환합니다.
   * @param {DashboardTemplate} currentTemplate - 현재 대시보드 템플릿
   * @param {ComponentTemplate} newComponent - 추가할 새 컴포넌트
   * @returns {DashboardTemplate} 업데이트된 대시보드 템플릿
   */
  const updateTemplateWithComponent = useCallback((currentTemplate, newComponent) => {
    const updatedComponents = [...(currentTemplate.components || []), newComponent];
    return {
      ...currentTemplate,
      components: updatedComponents
    };
  }, []);

  /**
   * 중복 컴포넌트가 있는지 확인합니다.
   * @param {DashboardTemplate} currentTemplate - 현재 대시보드 템플릿
   * @param {string} componentId - 확인할 컴포넌트 ID
   * @returns {ComponentTemplate|null} 중복 컴포넌트가 있으면 해당 컴포넌트, 없으면 null
   */
  const checkForDuplicateComponent = useCallback((currentTemplate, componentId) => {
    const existingComponent = currentTemplate.components?.find(comp => comp.id === componentId);
    if (existingComponent) {
      return existingComponent;
    }
    return null;
  }, []);

  /**
   * 대시보드 옵션을 적용합니다. 
   * - 템플릿 전체 교체의 경우: 백엔드 API로 ID 중복 체크 후 프론트엔드에만 반영
   * - 부분 옵션 수정의 경우: 프론트엔드에만 반영 (별도 저장 버튼으로)
   * @param {Object} updatedDashboardOptions - 업데이트된 대시보드 옵션 또는 전체 템플릿
   */
  const handleDashboardOptionsSave = useCallback(async (updatedDashboardOptions) => {
    const currentDashboardTemplate = dashboardTemplateRef.current;
    
    if (!currentDashboardTemplate) {
      console.error("[useDashboardActions] dashboardTemplate is null, cannot apply options.");
      showErrorMessage('대시보드 데이터가 로드되지 않아 옵션을 적용할 수 없습니다.');
      return;
    }

    try {
      // 템플릿 전체 교체인지 부분 옵션 수정인지 판단
      const isFullTemplateReplacement = updatedDashboardOptions.components && Array.isArray(updatedDashboardOptions.components);
      
      let updatedTemplate;
      
      if (isFullTemplateReplacement) {
        // 템플릿 전체 교체의 경우 (템플릿 탭에서 편집한 경우)
        updatedTemplate = {
          ...updatedDashboardOptions,
          dashboardId: updatedDashboardOptions.dashboardId || 
                      updatedDashboardOptions.dashboard_id || 
                      currentDashboardTemplate.dashboard_id || 
                      currentDashboardTemplate.dashboardId
        };
        
        // user, tenant 등 메타데이터는 기존 것 유지
        if (currentDashboardTemplate.user) updatedTemplate.user = currentDashboardTemplate.user;
        if (currentDashboardTemplate.tenant) updatedTemplate.tenant = currentDashboardTemplate.tenant;
        if (currentDashboardTemplate.id) updatedTemplate.id = currentDashboardTemplate.id;
      } else {
        // 부분 옵션 수정의 경우 (일반 옵션 탭에서 편집한 경우)
        updatedTemplate = {
          ...currentDashboardTemplate, 
          ...updatedDashboardOptions, 
          dashboardId: currentDashboardTemplate.dashboard_id || currentDashboardTemplate.dashboardId, 
          components: currentDashboardTemplate.components,
          layout: updatedDashboardOptions.layout || currentDashboardTemplate.layout,
          // is_hidden_in_list가 있으면 명시적으로 포함
          is_hidden_in_list: updatedDashboardOptions.is_hidden_in_list !== undefined ? 
                            updatedDashboardOptions.is_hidden_in_list : 
                            currentDashboardTemplate.is_hidden_in_list,
          // is_common_dashboard가 있으면 명시적으로 포함
          is_common_dashboard: updatedDashboardOptions.is_common_dashboard !== undefined ? 
                              updatedDashboardOptions.is_common_dashboard : 
                              currentDashboardTemplate.is_common_dashboard
        };
        
        // is_common_dashboard 값에 따라 user, user_id 필드 업데이트 (메모리 상태 반영)
        if (updatedDashboardOptions.is_common_dashboard !== undefined) {
          console.log('[useDashboardActions] is_common_dashboard 처리:', {
            is_common: updatedDashboardOptions.is_common_dashboard,
            current_user: currentDashboardTemplate.user,
            current_user_id: currentDashboardTemplate.user_id
          });
          
          if (updatedDashboardOptions.is_common_dashboard) {
            // 공통 대시보드: user와 user_id를 null로
            updatedTemplate.user = null;
            updatedTemplate.user_id = null;
            console.log('[useDashboardActions] 공통 대시보드로 설정: user=null, user_id=null');
          } else {
            // 개인 대시보드: 기존 user 정보 유지 (실제 user 정보는 저장 시 백엔드에서 설정)
            // user_id는 현재 템플릿의 것을 유지하거나, 없으면 undefined로 (저장 시 백엔드가 설정)
            updatedTemplate.user = currentDashboardTemplate.user;
            updatedTemplate.user_id = currentDashboardTemplate.user_id;
            console.log('[useDashboardActions] 개인 대시보드로 설정: user=', currentDashboardTemplate.user, ', user_id=', currentDashboardTemplate.user_id);
          }
        }
      }
      
      // 템플릿 전체 교체의 경우 대시보드 ID 중복 체크 (백엔드 API 사용)
      if (isFullTemplateReplacement) {
        const newDashboardId = updatedTemplate.dashboard_id || updatedTemplate.dashboardId;
        const currentDashboardId = currentDashboardTemplate.dashboard_id || currentDashboardTemplate.dashboardId;
        
                 // ID 변경 여부에 따른 분기 처리
         if (newDashboardId !== currentDashboardId) {
           // 케이스 1: ID가 변경된 경우 - 먼저 대상 ID 충돌 체크
           try {
             // 1단계: 새 대시보드 ID가 이미 존재하는지 체크
             const validateResponse = await DashboardService.validateDashboardId(newDashboardId, currentDashboardId);
             
             if (validateResponse.success) {
               const { is_duplicate, conflict_info } = validateResponse.data;
               
               if (is_duplicate && conflict_info) {
                 // 새 대시보드 ID가 이미 존재 - 기존 충돌 모달 열기 (덮어쓰기/새로 생성 선택)
                 openConflictModal(conflict_info, updatedTemplate);
                 return; // 충돌 모달에서 사용자 선택을 기다림
               } else {
                 // 새 대시보드 ID 사용 가능 - 2단계: 새 대시보드 생성
                 const response = await DashboardService.saveDashboardWithOptions(
                   updatedTemplate,
                   { 
                     generate_new_id: false, // 명시적으로 지정된 ID 사용
                     force_replace: false,
                     current_dashboard_id: currentDashboardId
                   }
                 );
                 
                 if (response.success) {
                   // 기존 대시보드 처리 로직 (충돌 모달을 활용)
                   const hasCharts = currentDashboardTemplate?.components && currentDashboardTemplate.components.length > 0;
                   
                   if (openConflictModal) {
                     // 충돌 모달을 활용하여 후속 처리 위임
                     openConflictModal({
                       conflictType: 'dashboard_id_changed',
                       originalDashboardId: currentDashboardId,
                       newDashboardId: newDashboardId,
                       hasCharts,
                       newTemplate: updatedTemplate
                     });
                     return; // 충돌 모달에서 사용자 선택을 기다림
                   } else {
                     console.warn('[useDashboardActions] openConflictModal 함수가 없어 추가 처리를 수행할 수 없습니다.');
                     // 기본적으로는 로컬 상태만 업데이트
                     setDashboardTemplateAndUpdateData(updatedTemplate);
                     // showSuccessMessage('새 대시보드가 생성되었습니다.'); // 모달 제거
                     return;
                   }
                 } else {
                   console.error('[useDashboardActions] 새 대시보드 저장 실패:', response.error);
                   showErrorMessage(`새 대시보드 저장 실패: ${response.error}`);
                   return;
                 }
               }
             } else {
               console.error("[useDashboardActions] 대시보드 ID 검증 실패:", validateResponse.error);
               showErrorMessage(`대시보드 ID 검증에 실패했습니다: ${validateResponse.error}`);
               return;
             }
           } catch (error) {
             console.error('[useDashboardActions] 대시보드 ID 변경 처리 중 오류:', error);
             showErrorMessage('대시보드 ID 변경 처리 중 오류가 발생했습니다.');
             return;
           }
         } else {
           // 케이스 2: ID가 같은 경우 - 기존 충돌 체크 로직 유지
           try {
             // 백엔드 API로 중복 체크 (기존 로직 복원)
             const response = await DashboardService.validateDashboardId(newDashboardId, currentDashboardId);
             
             if (response.success) {
               const { is_duplicate, conflict_info } = response.data;
               
               if (is_duplicate && conflict_info) {
                 // 기존 충돌 발생 - 기존 충돌 모달 열기 (덮어쓰기/새로 생성 선택)
                 openConflictModal(conflict_info, updatedTemplate);
                 return; // 충돌 모달에서 사용자 선택을 기다림
               } else {
               }
             } else {
               console.error("[useDashboardActions] 대시보드 ID 중복 체크 실패:", response.error);
               showErrorMessage(`대시보드 ID 중복 체크에 실패했습니다: ${response.error}`);
               return;
             }
           } catch (error) {
             console.error("[useDashboardActions] 대시보드 ID 중복 체크 중 오류:", error);
             showErrorMessage(`대시보드 ID 중복 체크 중 오류가 발생했습니다: ${error.message}`);
             return;
           }
         }
        
        // 프론트엔드에만 적용 (백엔드 저장 제거)
        const { _autoResizeNeeded, ...cleanedTemplate } = updatedTemplate;
        setDashboardTemplateAndUpdateData(cleanedTemplate);
        // 전체 템플릿 교체 시 새 템플릿의 position을 기준으로 레이아웃 재구성
        try {
          const componentsArray = (cleanedTemplate.components || []).filter(comp => comp && comp.id);
          const gridLayout = componentsArray.map(comp => {
            const pos = comp.position || {};
            return {
              i: comp.id,
              x: Number.isFinite(pos.x) ? pos.x : 0,
              y: Number.isFinite(pos.y) ? pos.y : 0,
              w: Number.isFinite(pos.w) ? pos.w : 3,
              h: Number.isFinite(pos.h) ? pos.h : 2,
              minW: 2,
              minH: 2
            };
          });
          setLayouts({
            lg: gridLayout,
            md: gridLayout,
            sm: gridLayout,
            xs: gridLayout,
            xxs: gridLayout
          });
        } catch (e) {
          console.warn('[useDashboardActions] 레이아웃 재구성 중 오류:', e);
        }
        // showSuccessMessage('대시보드 템플릿이 적용되었습니다.'); // 모달 제거
      } else {
        // 부분 옵션 수정의 경우 로컬 상태만 업데이트 (기존 동작 유지)
        const finalTemplate = updatedTemplate;
        
        // is_hidden_in_list가 변경된 경우 대시보드 목록 갱신 필요
        // 하지만 setHasLayoutChanged를 true로 설정하여 저장 버튼이 나타나도록 함
        const isHiddenChanged = currentDashboardTemplate.is_hidden_in_list !== updatedDashboardOptions.is_hidden_in_list;
        if (isHiddenChanged && updatedDashboardOptions.is_hidden_in_list !== undefined) {
          // 저장 버튼이 나타나도록 플래그 설정
          if (editMode && setHasLayoutChanged) {
            setHasLayoutChanged(true);
          }
          // 대시보드 목록 갱신은 저장 후에 자동으로 처리되도록 함
        }
        
        // 자동 크기 조정이 필요한 경우 Dashboard 컴포넌트가 처리하도록 _autoResizeNeeded 플래그 유지
        if (updatedDashboardOptions._autoResizeNeeded) {
          console.log('[useDashboardActions] 템플릿 업데이트 (자동 리사이즈):', {
            is_common_dashboard: finalTemplate.is_common_dashboard,
            user: finalTemplate.user,
            user_id: finalTemplate.user_id
          });
          setDashboardTemplateAndUpdateData(finalTemplate);
          // showSuccessMessage('대시보드 옵션이 적용되었습니다. 차트 크기가 새로운 그리드에 비례적으로 조정됩니다.'); // 모달 제거
        } else {
          // 일반적인 변경의 경우 플래그 제거 후 업데이트
          const { _autoResizeNeeded, ...cleanedTemplate } = finalTemplate;
          console.log('[useDashboardActions] 템플릿 업데이트 (일반):', {
            is_common_dashboard: cleanedTemplate.is_common_dashboard,
            user: cleanedTemplate.user,
            user_id: cleanedTemplate.user_id
          });
          setDashboardTemplateAndUpdateData(cleanedTemplate);
          // showSuccessMessage('대시보드 옵션이 적용되었습니다.'); // 모달 제거
        }
      }

      // 변경사항이 있음을 표시하여 저장 버튼이 나타나도록 설정
      if (editMode && setHasLayoutChanged) {
        setHasLayoutChanged(true);
      }
      
    } catch (error) {
      console.error("[useDashboardActions] 대시보드 옵션 적용 오류:", error);
      showErrorMessage(`대시보드 옵션 적용에 실패했습니다: ${error.message}`);
    }
  },
  [setDashboardTemplateAndUpdateData, editMode, setHasLayoutChanged]);

  /**
   * 템플릿 저장 콜백을 처리합니다.
   * @param {DashboardTemplate} updatedTemplateFromModal - 모달에서 업데이트된 템플릿
   * @param {Object} changeInfo - 변경 정보 (dashboardIdChanged, originalDashboardId, newDashboardId)
   */
  const handleTemplateSaved = useCallback(async (updatedTemplateFromModal, changeInfo = {}) => {
    const { dashboardIdChanged, originalDashboardId, newDashboardId } = changeInfo;
    
    if (savePreActionState) {
      const description = `템플릿 직접 편집: ${updatedTemplateFromModal?.title || '대시보드'}`;
      savePreActionState('template_edit', 'template', description);
    }
    
    // dashboard_id가 변경된 경우 새 대시보드 생성 플로우 실행
    if (dashboardIdChanged && newDashboardId && originalDashboardId) {
      try {
        // 1. 새 대시보드로 저장
        const response = await DashboardService.saveDashboardWithOptions(
          updatedTemplateFromModal,
          { 
            generate_new_id: false, // 명시적으로 지정된 ID 사용
            force_replace: false,
            current_dashboard_id: originalDashboardId
          }
        );
        
        if (response.success) {
          // 2. 기존 대시보드 처리 로직 (충돌 모달을 활용)
          const currentTemplate = dashboardTemplateRef.current;
          const hasCharts = currentTemplate?.components && currentTemplate.components.length > 0;
          
          if (openConflictModal) {
            // 충돌 모달을 활용하여 후속 처리 위임
            openConflictModal({
              conflictType: 'dashboard_id_changed',
              originalDashboardId,
              newDashboardId,
              hasCharts,
              newTemplate: updatedTemplateFromModal
            });
          } else {
            console.warn('[useDashboardActions] openConflictModal 함수가 없어 추가 처리를 수행할 수 없습니다.');
            // 기본적으로는 로컬 상태만 업데이트
            setDashboardTemplateAndUpdateData(updatedTemplateFromModal);
          }
        } else {
          console.error('[useDashboardActions] 새 대시보드 저장 실패:', response.error);
          showErrorMessage(`새 대시보드 저장 실패: ${response.error}`);
        }
      } catch (error) {
        console.error('[useDashboardActions] 새 대시보드 생성 중 오류:', error);
        showErrorMessage('새 대시보드 생성 중 오류가 발생했습니다.');
      }
    } else {
      // 기존 로직: dashboard_id가 변경되지 않은 경우
      setDashboardTemplateAndUpdateData(updatedTemplateFromModal);
      
      // 템플릿 저장 후 hasLayoutChanged 상태 업데이트 (저장 버튼 표시를 위해)
      if (editMode && setHasLayoutChanged) {
        setHasLayoutChanged(true);
      }
    }
  }, [setDashboardTemplateAndUpdateData, savePreActionState, editMode, setHasLayoutChanged, openConflictModal]);

  /**
   * 컴포넌트 더블클릭을 처리합니다.
   * @param {ComponentTemplate} component - 더블클릭된 컴포넌트
   */
  const handleComponentDoubleClick = useCallback((component) => {
    if (!editMode || !component) return;

    try {
      // 모든 차트 타입을 공용 컴포넌트 모달로 편집
      openNLComponentModal(component);
    } catch (error) {
      console.error('[useDashboardActions] 컴포넌트 더블클릭 처리 중 오류:', error);
    }
  }, [editMode, openNLComponentModal]);

  /**
   * 컴포넌트 생성 또는 업데이트를 처리합니다.
   * @param {ComponentTemplate} componentTemplate - 컴포넌트 템플릿
   * @param {boolean} isUpdate - 업데이트 여부
   */
  const handleComponentCreated = useCallback(async (componentTemplate, isUpdate = false) => {
    try {
      const currentDashboardTemplate = dashboardTemplateRef.current;
      
      if (!currentDashboardTemplate) {
        console.warn('[useDashboardActions] dashboardTemplate이 없습니다. 기본 템플릿을 생성합니다.');
        showErrorMessage('대시보드 템플릿을 먼저 로드해주세요.');
        return;
      }
      
      if (savePreActionState) {
        const actionType = isUpdate ? 'update_component' : 'create_component';
        const description = isUpdate 
          ? `컴포넌트 수정: ${componentTemplate.title || componentTemplate.id}` 
          : `컴포넌트 생성: ${componentTemplate.title || componentTemplate.id}`;
        savePreActionState(actionType, componentTemplate.id, description);
      }
      
      // 1. 로컬 상태 업데이트
      const updatedTemplate = { ...currentDashboardTemplate };
      let updatedComponents = [...(updatedTemplate.components || [])];
      
      if (isUpdate && componentToUpdate) {
        const index = updatedComponents.findIndex(comp => comp.id === componentToUpdate.id);
        if (index >= 0) {
          updatedComponents[index] = componentTemplate;
        } else {
          updatedComponents.push(componentTemplate);
        }
      } else {
        updatedComponents.push(componentTemplate);
      }
      
      updatedTemplate.components = updatedComponents;
      
      // 2. 레이아웃 업데이트 (포지션 동기화 포함)
      if (isUpdate && componentToUpdate) {
        if (componentToUpdate.id !== componentTemplate.id) {
          removeComponentFromLayout(componentToUpdate.id);
          addComponentToLayout(componentTemplate);
        } else {
          const pos = componentTemplate.position;
          if (pos && typeof pos === 'object') {
            // 모든 브레이크포인트 레이아웃에 x,y,w,h 동기화
            const applySync = (sourceLayouts) => {
              const syncOne = (arr = []) => arr.map(l => l.i === componentTemplate.id ? {
                ...l,
                x: Number.isFinite(pos.x) ? pos.x : l.x,
                y: Number.isFinite(pos.y) ? pos.y : l.y,
                w: Number.isFinite(pos.w) ? pos.w : l.w,
                h: Number.isFinite(pos.h) ? pos.h : l.h
              } : l);
              return {
                lg: syncOne(sourceLayouts.lg),
                md: syncOne(sourceLayouts.md),
                sm: syncOne(sourceLayouts.sm),
                xs: syncOne(sourceLayouts.xs),
                xxs: syncOne(sourceLayouts.xxs)
              };
            };
            setLayouts(prev => applySync(prev || { lg: [], md: [], sm: [], xs: [], xxs: [] }));
          } else {
            // pos가 없으면 기존 w/h 최소 동기화 로직 유지
            const layoutItem = layouts.lg.find(item => item.i === componentTemplate.id);
            if (layoutItem && (layoutItem.w !== componentTemplate.position?.w || layoutItem.h !== componentTemplate.position?.h)) {
              const newLayouts = layouts.lg.map(l => l.i === componentTemplate.id ? {
                ...l,
                w: componentTemplate.position?.w || l.w,
                h: componentTemplate.position?.h || l.h
              } : l);
              setLayouts({ lg: newLayouts });
            }
          }
        }
      } else {
        // 신규 추가 시에도 레이아웃에 추가 (내부에서 배치 계산)
        addComponentToLayout(componentTemplate);
      }
      
      // 3. 로컬 상태 즉시 업데이트
      setDashboardTemplateAndUpdateData(updatedTemplate);
      
      // 4. 차트 수정 후 hasLayoutChanged 상태 업데이트 (저장 버튼 표시를 위해)
      if (editMode && setHasLayoutChanged) {
        setHasLayoutChanged(true);
      }
      
      // showSuccessMessage(isUpdate ? '컴포넌트가 성공적으로 업데이트되었습니다.' : '컴포넌트가 성공적으로 추가되었습니다.'); // 모달 제거
      
      closeNLComponentModal();
    } catch (err) {
      console.error('[useDashboardActions] 컴포넌트 적용 중 오류:', err);
      showErrorMessage('컴포넌트 적용 중 오류가 발생했습니다.');
      closeNLComponentModal();
    }
  }, [componentToUpdate, layouts, setDashboardTemplateAndUpdateData, addComponentToLayout, removeComponentFromLayout, setLayouts, closeNLComponentModal, savePreActionState]);

  /**
   * 컴포넌트를 삭제합니다.
   * @param {string} componentId - 삭제할 컴포넌트 ID
   */
  const deleteComponent = useCallback((componentId, { skipHistorySaving = false } = {}) => {
    const currentDashboardTemplate = dashboardTemplateRef.current;
    
    if (!componentId || !currentDashboardTemplate || !currentDashboardTemplate.components) {
      console.error("[useDashboardActions] 유효하지 않은 컴포넌트 ID 또는 템플릿이 없음");
      return;
    }
    
    try {
      const updatedComponents = currentDashboardTemplate.components.filter(
        comp => comp.id !== componentId
      );
      
      const updatedTemplate = {
        ...currentDashboardTemplate,
        components: updatedComponents
      };
      
      // 삭제 전 상태를 히스토리에 저장 (외부에서 이미 저장했으면 skip)
      if (!skipHistorySaving && savePreActionState) {
        const desc = `차트 삭제: ${componentId}`;
        savePreActionState('delete_component', componentId, desc);
      }

      setDashboardTemplateAndUpdateData(updatedTemplate);
      removeComponentFromLayout(componentId, true /* skipHistorySaving */);
      // 저장 트리거를 위해 레이아웃 변경 플래그 설정
      if (setHasLayoutChanged) {
        setHasLayoutChanged(true);
      }
      
    } catch (err) {
      console.error("[useDashboardActions] 컴포넌트 삭제 중 오류:", err);
      showErrorMessage("컴포넌트 삭제 중 오류가 발생했습니다.");
    }
  }, [setDashboardTemplateAndUpdateData, removeComponentFromLayout]);

  /**
   * 새 차트를 추가합니다.
   */
  const addNewChart = useCallback(async () => {
    const currentDashboardTemplate = dashboardTemplateRef.current;
    
    if (!currentDashboardTemplate) {
      console.error("[useDashboardActions] 템플릿이 없습니다.");
      showErrorMessage("대시보드 템플릿을 먼저 로드해주세요.");
      return;
    }
    
    try {
      // 차트 종류 선택을 위해 NLComponentModal을 새 컴포넌트 모드로 열기
      // existingComponent를 null로 설정하여 새 컴포넌트 생성 모드로 진입
      openNLComponentModal(null);
      
      // 기존의 자동 차트 생성 로직은 주석 처리
      /*
      const newChartTemplate = createNewChartTemplate();
      const newLayoutItem = addComponentToLayout(newChartTemplate);

      const finalNewComponent = {
        ...newChartTemplate,
        position: { x: newLayoutItem.x, y: newLayoutItem.y, w: newLayoutItem.w, h: newLayoutItem.h },
      };
      delete finalNewComponent.w;
      delete finalNewComponent.h;

      const updatedComponents = [...(currentDashboardTemplate.components || []), finalNewComponent];
      const updatedTemplate = {
        ...currentDashboardTemplate,
        components: updatedComponents
      };
      
      setDashboardTemplateAndUpdateData(updatedTemplate); 
      // 저장 트리거를 위해 레이아웃 변경 플래그 설정
      if (setHasLayoutChanged) {
        setHasLayoutChanged(true);
      }
      
      // 새로 추가된 차트 위치로 스크롤
      scrollToNewComponent(finalNewComponent.position, currentDashboardTemplate);
      
      if (!editMode) {
        toggleEditMode(); 
      }
      */
    } catch (err) {
      console.error("[useDashboardActions] 차트 선택 모달 열기 중 오류:", err);
      showErrorMessage("차트 선택 화면을 여는 중 오류가 발생했습니다.");
    }
  }, [openNLComponentModal]);

  /**
   * LLM 컴포넌트를 로컬 상태에만 추가합니다 (백엔드 저장 없음).
   * "편집 버튼 누르고 차트 추가"와 동일한 상태를 만듭니다.
   * @param {ComponentTemplate} componentTemplate - 추가할 컴포넌트 템플릿
   * @returns {ComponentTemplate} 추가된 컴포넌트 (position 정보 포함)
   * @throws {Error} 대시보드 템플릿이 로드되지 않은 경우
   */
  const addComponentToLocalState = useCallback((componentTemplate) => {
    const currentDashboardTemplate = dashboardTemplateRef.current;
    
    // 1. 기본 유효성 검사
    if (!currentDashboardTemplate) {
      console.error("[useDashboardActions] 템플릿이 없습니다.");
      throw new Error("대시보드 템플릿을 먼저 로드해주세요.");
    }
    
    try {
      // 2. 중복 컴포넌트 체크
      const duplicateComponent = checkForDuplicateComponent(currentDashboardTemplate, componentTemplate.id);
      if (duplicateComponent) {
        return duplicateComponent;
      }
      
      // 3. 편집 모드 진입 처리
      const wasEditMode = editMode;
      handleEditModeEntry(wasEditMode);
      
      // 4. 레이아웃에 컴포넌트 추가 (히스토리 저장 없음, 강제 하단 배치)
      const newLayoutItem = addComponentToLayout(componentTemplate, true, true, currentDashboardTemplate); // skipHistorySaving = true, forceBottomPlacement = true, templateData = currentDashboardTemplate
      
      // 5. 최종 컴포넌트 생성 (position 정보 포함)
      const finalNewComponent = createFinalComponent(componentTemplate, newLayoutItem);

      // 6. 템플릿 업데이트
      const updatedTemplate = updateTemplateWithComponent(currentDashboardTemplate, finalNewComponent);
      
      // 7. 히스토리 저장 (템플릿 업데이트 직전)
      if (savePreActionState) {
        const description = `챗봇 차트 추가: ${componentTemplate.id}`;
        savePreActionState('chatbot_add_component', componentTemplate.id, description, true); // forceMode = true
      }
      
      // 8. 상태 업데이트
      setDashboardTemplateAndUpdateData(updatedTemplate);
      
      // 9. 차트 추가 후 hasLayoutChanged 상태 업데이트 (저장 버튼 표시를 위해)
      if (setHasLayoutChanged) {
        setHasLayoutChanged(true);
      }
      
      // 새로 추가된 컴포넌트 위치로 스크롤
      scrollToNewComponent(finalNewComponent.position, currentDashboardTemplate);
      
      return finalNewComponent;
      
    } catch (err) {
      console.error("[useDashboardActions] LLM 컴포넌트 로컬 추가 중 오류:", err);
      throw err;
    }
  }, [
    editMode, 
    savePreActionState, 
    addComponentToLayout, 
    setDashboardTemplateAndUpdateData,
    checkForDuplicateComponent,
    handleEditModeEntry,
    createFinalComponent,
    updateTemplateWithComponent
  ]);

  return {
    handleDashboardOptionsSave,
    handleTemplateSaved,
    handleComponentDoubleClick,
    handleComponentCreated,
    deleteComponent,
    addNewChart,
    addComponentToLocalState
  };
}; 